import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from '../lib/axios';
import { 
  Activity, AlertCircle, CheckCircle, Clock, XCircle, 
  Loader2, Play, Square, RefreshCw, Timer, Zap,
  TrendingUp, Database, Calendar, ChevronDown, ChevronUp,
  User, ExternalLink, BarChart3
} from 'lucide-react';
import JobResumenModal from '../components/JobResumenModal';

// Tipos de job que procesan liquids.liquidaciones (enlazables al listado / con resumen)
const TIPOS_PROCESO_LIQUIDACION_DECOMISION = ['liquidacion', 'decomision', 'liquidacion_batch', 'decomision_batch', 'liquidacion_volumen_cups'];

const MESES_LABEL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const JobsPage = () => {
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [resumenJobId, setResumenJobId] = useState(null);
  const [periodoVolumenCupsSeleccionado, setPeriodoVolumenCupsSeleccionado] = useState('');
  const [soloVolumenCups, setSoloVolumenCups] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Query para obtener la lista de jobs
  const { data: jobsData, isLoading, refetch } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const res = await axios.get('/api/jobs/');
      return res.data;
    },
    refetchInterval: 5000, // Refrescar cada 5 segundos
  });

  // Query para obtener estadísticas
  const { data: statsData } = useQuery({
    queryKey: ['jobs-stats'],
    queryFn: async () => {
      const res = await axios.get('/api/jobs/stats');
      return res.data;
    },
    refetchInterval: 5000,
  });

  // Periodos (año/mes) de liquidaciones de Volumen de CUPS: alimenta tanto el
  // selector de "qué mes calcular" como la vista de "meses ya calculados"
  const { data: periodosVolumenCupsData } = useQuery({
    queryKey: ['periodos-volumen-cups'],
    queryFn: async () => {
      const res = await axios.get('/api/liquidaciones/volumen-cups/periodos');
      return res.data;
    },
    refetchInterval: 30000,
  });
  const periodosVolumenCups = periodosVolumenCupsData?.data || [];
  // Accionables: periodo ya vencido y con algo por calcular (pendiente o error)
  const periodosVolumenCupsPendientes = periodosVolumenCups.filter(
    (p) => p.vencido && (p.pendiente > 0 || p.error > 0)
  );
  // Informativos: periodos con al menos una línea ya calculada
  const periodosVolumenCupsCalculados = periodosVolumenCups.filter((p) => p.calculada > 0);

  // Mutación para cancelar un job
  const cancelJobMutation = useMutation({
    mutationFn: async (jobId) => {
      const res = await axios.post(`/api/jobs/${jobId}/cancel`);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Job cancelado exitosamente');
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al cancelar el job');
    },
  });

  // Mutación para relanzar una sincronización ya finalizada (completada o con error)
  const relaunchSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post('/api/liquidaciones/sync/start');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Sincronización encolada');
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs-stats']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al relanzar la sincronización');
    },
  });

  // Mutación para lanzar el cálculo de liquidaciones de Volumen de CUPS (AUREUM/RASTREATOR/...)
  // de un periodo (año+mes) dado. Proceso independiente del motor genérico de liquidación.
  const calcularVolumenCupsMutation = useMutation({
    mutationFn: async ({ anio, mes }) => {
      const res = await axios.post('/api/liquidaciones/volumen-cups/calcular/start', null, {
        params: { anio, mes }
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Cálculo de Volumen CUPS encolado');
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs-stats']);
      queryClient.invalidateQueries(['periodos-volumen-cups']);
      setPeriodoVolumenCupsSeleccionado('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al lanzar el cálculo de Volumen CUPS');
    },
  });

  const jobs = jobsData?.data || [];
  const stats = statsData?.data || {};
  const jobsVisibles = soloVolumenCups
    ? jobs.filter((job) => job.tipo_proceso === 'liquidacion_volumen_cups')
    : jobs;

  const handleCancelJob = (jobId) => {
    if (window.confirm('¿Estás seguro de que quieres cancelar este proceso?')) {
      cancelJobMutation.mutate(jobId);
    }
  };

  const handleRelaunchSync = () => {
    relaunchSyncMutation.mutate();
  };

  const handleCalcularVolumenCups = () => {
    if (!periodoVolumenCupsSeleccionado) return;
    const [anio, mes] = periodoVolumenCupsSeleccionado.split('-').map(Number);
    calcularVolumenCupsMutation.mutate({ anio, mes });
  };

  const toggleExpandJob = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-600" />
              Procesos en Segundo Plano
            </h1>
            <p className="text-gray-600 mt-2">
              Monitoreo y control de jobs asíncronos (sincronización, liquidación, decomisión)
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatCard
          title="Total"
          value={stats.total || 0}
          icon={Database}
          color="gray"
        />
        <StatCard
          title="Pendientes"
          value={stats.pending || 0}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="En Ejecución"
          value={stats.running || 0}
          icon={Play}
          color="blue"
        />
        <StatCard
          title="Completados"
          value={stats.completed || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Errores"
          value={stats.error || 0}
          icon={AlertCircle}
          color="red"
        />
        <StatCard
          title="Cancelados"
          value={stats.cancelled || 0}
          icon={XCircle}
          color="orange"
        />
      </div>

      {/* Panel: Liquidación por Volumen de CUPS (AUREUM/RASTREATOR/...) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Liquidación por Volumen de CUPS</h2>
            <p className="text-sm text-gray-600 mt-1">
              Proceso independiente del cálculo general: elige un mes ya vencido con
              liquidaciones pendientes de esquemas AUREUM/RASTREATOR y lánzalo.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={soloVolumenCups}
                onChange={(e) => setSoloVolumenCups(e.target.checked)}
              />
              Ver solo estos procesos
            </label>
            <select
              value={periodoVolumenCupsSeleccionado}
              onChange={(e) => setPeriodoVolumenCupsSeleccionado(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[220px]"
            >
              <option value="">
                {periodosVolumenCupsPendientes.length === 0
                  ? 'No hay meses pendientes'
                  : 'Selecciona un mes pendiente'}
              </option>
              {periodosVolumenCupsPendientes.map((periodo) => (
                <option key={`${periodo.anio}-${periodo.mes}`} value={`${periodo.anio}-${periodo.mes}`}>
                  {MESES_LABEL[periodo.mes - 1]} {periodo.anio} ({periodo.pendiente + periodo.error} pendientes)
                </option>
              ))}
            </select>
            <button
              onClick={handleCalcularVolumenCups}
              disabled={calcularVolumenCupsMutation.isPending || !periodoVolumenCupsSeleccionado}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              Calcular mes seleccionado
            </button>
          </div>
        </div>

        {/* Meses ya calculados (informativo) */}
        {periodosVolumenCupsCalculados.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Meses ya calculados</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase">
                    <th className="pr-4 py-1">Periodo</th>
                    <th className="pr-4 py-1 text-right">Calculadas</th>
                    <th className="pr-4 py-1 text-right">Pendientes</th>
                    <th className="pr-4 py-1 text-right">Error</th>
                    <th className="pr-4 py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {periodosVolumenCupsCalculados.map((periodo) => (
                    <tr key={`${periodo.anio}-${periodo.mes}`} className="border-t border-gray-100">
                      <td className="pr-4 py-1 font-medium text-gray-800">
                        {MESES_LABEL[periodo.mes - 1]} {periodo.anio}
                        {!periodo.vencido && (
                          <span className="ml-2 text-xs text-gray-400 italic">(en curso)</span>
                        )}
                      </td>
                      <td className="pr-4 py-1 text-right text-green-700">{periodo.calculada}</td>
                      <td className="pr-4 py-1 text-right text-yellow-700">{periodo.pendiente}</td>
                      <td className="pr-4 py-1 text-right text-red-700">{periodo.error}</td>
                      <td className="pr-4 py-1 text-right text-gray-600">{periodo.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Tabla de Jobs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Lista de Procesos</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Cargando jobs...</span>
          </div>
        ) : jobsVisibles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mb-3 opacity-50" />
            <p>No hay jobs registrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo de Proceso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progreso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Velocidad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Inicio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tiempo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobsVisibles.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <TipoProcesoBadge tipo={job.tipo_proceso} />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          {job.creado_por_nombre || job.creado_por_email || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <EstadoBadge estado={job.estado} debeCancelar={job.debe_cancelar} />
                      </td>
                      <td className="px-4 py-3">
                        <ProgressBar
                          progreso={job.progreso_actual}
                          total={job.progreso_total}
                          porcentaje={job.porcentaje}
                          exitosos={job.registros_exitosos}
                          fallidos={job.registros_fallidos}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {job.velocidad_registros_seg > 0 ? (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            {job.velocidad_registros_seg.toFixed(1)} reg/s
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {job.fecha_inicio ? formatDateTime(job.fecha_inicio) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {job.tiempo_transcurrido ? formatDuration(job.tiempo_transcurrido) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleExpandJob(job.id)}
                            className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                            title="Ver detalles"
                          >
                            {expandedJobId === job.id ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                          {(job.estado === 'pending' || job.estado === 'running') && (
                            <button
                              onClick={() => handleCancelJob(job.id)}
                              disabled={cancelJobMutation.isLoading || job.debe_cancelar}
                              className="p-1 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                              title={job.debe_cancelar ? 'Cancelación ya solicitada' : 'Cancelar job'}
                            >
                              <Square className="w-5 h-5" />
                            </button>
                          )}
                          {job.tipo_proceso === 'sync' && (job.estado === 'completed' || job.estado === 'error') && (
                            <button
                              onClick={handleRelaunchSync}
                              disabled={relaunchSyncMutation.isLoading}
                              className="p-1 text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                              title="Relanzar sincronización"
                            >
                              <Play className="w-5 h-5" />
                            </button>
                          )}
                          {TIPOS_PROCESO_LIQUIDACION_DECOMISION.includes(job.tipo_proceso) && (
                            <>
                              <button
                                onClick={() => navigate(`/liquidaciones?job_id=${job.id}`)}
                                className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                                title="Ver liquidaciones procesadas por este job"
                              >
                                <ExternalLink className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => setResumenJobId(job.id)}
                                className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                                title="Ver resumen del lote"
                              >
                                <BarChart3 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Fila expandida con detalles */}
                    {expandedJobId === job.id && (
                      <tr>
                        <td colSpan="8" className="px-4 py-4 bg-gray-50">
                          <JobDetails job={job} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <JobResumenModal
        isOpen={!!resumenJobId}
        onClose={() => setResumenJobId(null)}
        jobId={resumenJobId}
      />
    </div>
  );
};

// ==================== COMPONENTES AUXILIARES ====================

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    gray: 'bg-gray-100 border-gray-300 text-gray-900',
    yellow: 'bg-yellow-50 border-yellow-300 text-yellow-900',
    blue: 'bg-blue-50 border-blue-300 text-blue-900',
    green: 'bg-green-50 border-green-300 text-green-900',
    red: 'bg-red-50 border-red-300 text-red-900',
    orange: 'bg-orange-50 border-orange-300 text-orange-900',
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium opacity-75">{title}</span>
        <Icon className="w-5 h-5 opacity-75" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

const TipoProcesoBadge = ({ tipo }) => {
  const config = {
    sync: { label: 'Sincronización', color: 'bg-blue-100 text-blue-800' },
    liquidacion: { label: 'Liquidación', color: 'bg-green-100 text-green-800' },
    decomision: { label: 'Decomisión', color: 'bg-purple-100 text-purple-800' },
    liquidacion_volumen_cups: { label: 'Liquidación (Volumen CUPS)', color: 'bg-teal-100 text-teal-800' },
  };

  const { label, color } = config[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
};

const EstadoBadge = ({ estado, debeCancelar }) => {
  const config = {
    pending: { label: 'Pendiente', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
    running: { label: 'En Ejecución', icon: Play, color: 'bg-blue-100 text-blue-800' },
    completed: { label: 'Completado', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
    error: { label: 'Error', icon: AlertCircle, color: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Cancelado', icon: XCircle, color: 'bg-orange-100 text-orange-800' },
  };

  // El job sigue 'running' hasta que su propio bucle detecta debe_cancelar y se detiene
  const { label, icon: Icon, color } = estado === 'running' && debeCancelar
    ? { label: 'Cancelando...', icon: Loader2, color: 'bg-orange-100 text-orange-800' }
    : config[estado] || { label: estado, icon: Activity, color: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
};

const ProgressBar = ({ progreso, total, porcentaje, exitosos, fallidos }) => {
  if (total === 0) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-700">
          {progreso.toLocaleString()} / {total.toLocaleString()}
        </span>
        <span className="text-xs font-semibold text-gray-900">{porcentaje}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${porcentaje}%` }}
        />
      </div>
      {(exitosos > 0 || fallidos > 0) && (
        <div className="flex items-center gap-3 mt-1 text-xs">
          <span className="text-green-600">✓ {exitosos.toLocaleString()}</span>
          {fallidos > 0 && <span className="text-red-600">✗ {fallidos.toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
};

const JobDetails = ({ job }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Información General</h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">ID del Job:</span>
            <span className="font-mono text-xs text-gray-900">{job.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Fecha creación:</span>
            <span className="text-gray-900">{formatDateTime(job.created_at)}</span>
          </div>
          {job.fecha_fin && (
            <div className="flex justify-between">
              <span className="text-gray-600">Fecha fin:</span>
              <span className="text-gray-900">{formatDateTime(job.fecha_fin)}</span>
            </div>
          )}
          {job.worker_heartbeat && job.estado === 'running' && (
            <div className="flex justify-between">
              <span className="text-gray-600">Último heartbeat:</span>
              <span className="text-gray-900">{formatDateTime(job.worker_heartbeat)}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Estado del Proceso</h4>
        <div className="space-y-1 text-sm">
          {job.mensaje_progreso && (
            <div>
              <span className="text-gray-600">Mensaje:</span>
              <p className="text-gray-900 mt-1">{job.mensaje_progreso}</p>
            </div>
          )}
          {job.mensaje_error && (
            <div>
              <span className="text-red-600 font-semibold">Error:</span>
              <p className="text-red-700 mt-1">{job.mensaje_error}</p>
            </div>
          )}
        </div>
      </div>

      {job.parametros && Object.keys(job.parametros).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Parámetros</h4>
          <pre className="text-xs bg-gray-100 p-2 rounded border border-gray-300 overflow-x-auto">
            {JSON.stringify(job.parametros, null, 2)}
          </pre>
        </div>
      )}

      {job.resultado_final && Object.keys(job.resultado_final).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Resultado Final</h4>
          <pre className="text-xs bg-gray-100 p-2 rounded border border-gray-300 overflow-x-auto">
            {JSON.stringify(job.resultado_final, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ==================== UTILIDADES ====================

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export default JobsPage;
